import { sql } from "drizzle-orm";
import { 
  pgTable, 
  text, 
  varchar, 
  integer, 
  serial, 
  timestamp, 
  decimal,
  boolean,
  jsonb,
  unique,
  numeric,
  index
} from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";


export const emailProcessingLogs = pgTable("email_processing_logs", {
        id: serial().notNull(),
        tenantId: varchar("tenant_id").notNull(),
        emailAccountId: integer("email_account_id").notNull(),
        messageId: varchar("message_id").notNull(),
        fromAddress: varchar("from_address").notNull(),
        subject: text(),
        receivedAt: timestamp("received_at", { mode: 'string' }).notNull(),
        attachmentCount: integer("attachment_count").default(0),
        processedAttachments: integer("processed_attachments").default(0),
        ordersCreated: integer("orders_created").default(0),
        ocrResults: jsonb("ocr_results"),
        processingStatus: varchar("processing_status").default('pending'),
        errorMessage: text("error_message"),
        retryCount: integer("retry_count").default(0),
        createdAt: timestamp("created_at", { mode: 'string' }).defaultNow(),
        updatedAt: timestamp("updated_at", { mode: 'string' }).defaultNow(),
        documentType: varchar("document_type"),
        filename: varchar(),
        fileType: varchar("file_type"),
        fileSize: integer("file_size"),
        ocrConfidence: numeric("ocr_confidence"),
        rawText: text("raw_text"),
        metadata: jsonb("metadata"),
}, (table) => [
        index("email_processing_account_idx").using("btree", table.emailAccountId.asc().nullsLast().op("int4_ops")),
        index("email_processing_message_idx").using("btree", table.messageId.asc().nullsLast().op("text_ops")),
        index("email_processing_tenant_id_idx").using("btree", table.tenantId.asc().nullsLast().op("text_ops")),
]);

// Enhanced Users table for multi-tenancy
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  username: text("username"),
  password: text("password").notNull(),
  email: varchar("email", { length: 255 }).unique(),
  firstName: varchar("first_name", { length: 100 }),
  lastName: varchar("last_name", { length: 100 }),
  phone: varchar("phone", { length: 20 }),
  role: varchar("role", { length: 20 }).notNull().default("customer"), // admin, operator, customer
  tenantId: varchar("tenant_id", { length: 50 }), // null for customers, set for staff
  isActive: boolean("is_active").notNull().default(true),
  emailVerified: boolean("email_verified").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Tenants table for multi-tenancy
export const tenants = pgTable("tenants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantCode: varchar("tenant_code", { length: 20 }).unique(), // Format: TTID001, C001
  name: varchar("name", { length: 100 }).notNull(),
  domain: varchar("domain", { length: 100 }).unique(),
  contactEmail: varchar("contact_email", { length: 255 }),
  contactPhone: varchar("contact_phone", { length: 20 }),
  address: text("address"),
  isActive: boolean("is_active").notNull().default(true),
  settings: jsonb("settings"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Facilities table (enhanced sites with booking features)
export const facilities = pgTable("facilities", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  description: text("description"),
  address: text("address").notNull(),
  latitude: decimal("latitude", { precision: 10, scale: 8 }),
  longitude: decimal("longitude", { precision: 11, scale: 8 }),
  type: varchar("type", { length: 50 }).notNull(), // weighbridge, truck_stop, depot
  capacity: integer("capacity").notNull().default(50),
  hourlyRate: decimal("hourly_rate", { precision: 10, scale: 2 }),
  dailyRate: decimal("daily_rate", { precision: 10, scale: 2 }),
  amenities: text("amenities").array().default([]), // parking, wifi, shower, etc
  images: text("images").array().default([]), // image URLs
  operatingHours: jsonb("operating_hours"), // {monday: {open: "06:00", close: "22:00"}, ...}
  contactPhone: varchar("contact_phone", { length: 20 }),
  contactEmail: varchar("contact_email", { length: 255 }),
  isActive: boolean("is_active").notNull().default(true),
  isPublic: boolean("is_public").notNull().default(true), // visible to public for booking
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Services table
export const services = pgTable("services", {
  id: serial("id").primaryKey(),
  facilityId: integer("facility_id").notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  description: text("description"),
  category: varchar("category", { length: 50 }).notNull(), // food, parking, shower, wifi, fuel, wash
  price: decimal("price", { precision: 10, scale: 2 }).notNull(),
  priceType: varchar("price_type", { length: 20 }).notNull().default("fixed"), // fixed, hourly, daily
  isAvailable: boolean("is_available").notNull().default(true),
  maxCapacity: integer("max_capacity"),
  duration: integer("duration"), // in minutes for services with time limits
  requiresBooking: boolean("requires_booking").notNull().default(false),
  icon: varchar("icon", { length: 50 }), // icon name for UI
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Bookings table
export const bookings = pgTable("bookings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id", { length: 50 }).notNull(),
  facilityId: integer("facility_id").notNull(),
  bookingNumber: varchar("booking_number", { length: 20 }).unique().notNull(),
  vehiclePlate: varchar("vehicle_plate", { length: 20 }).notNull(),
  driverName: varchar("driver_name", { length: 100 }).notNull(),
  driverPhone: varchar("driver_phone", { length: 20 }),
  
  // Booking details
  checkInDate: timestamp("check_in_date").notNull(),
  checkOutDate: timestamp("check_out_date"),
  estimatedDuration: integer("estimated_duration"), // in hours
  actualCheckIn: timestamp("actual_check_in"),
  actualCheckOut: timestamp("actual_check_out"),
  
  // Pricing
  basePrice: decimal("base_price", { precision: 10, scale: 2 }).notNull(),
  servicesTotal: decimal("services_total", { precision: 10, scale: 2 }).notNull().default("0"),
  totalAmount: decimal("total_amount", { precision: 10, scale: 2 }).notNull(),
  
  // Status and payment
  status: varchar("status", { length: 20 }).notNull().default("pending"), // pending, confirmed, checked_in, completed, cancelled
  paymentStatus: varchar("payment_status", { length: 20 }).notNull().default("pending"), // pending, paid, refunded
  // paymentIntentId: varchar("payment_intent_id", { length: 100 }), // Commented out for now - Stripe disabled
  
  // Additional info
  specialRequests: text("special_requests"),
  notes: text("notes"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Service bookings (many-to-many for booking services)
export const serviceBookings = pgTable("service_bookings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  bookingId: varchar("booking_id", { length: 50 }).notNull(),
  serviceId: integer("service_id").notNull(),
  quantity: integer("quantity").notNull().default(1),
  unitPrice: decimal("unit_price", { precision: 10, scale: 2 }).notNull(),
  totalPrice: decimal("total_price", { precision: 10, scale: 2 }).notNull(),
  scheduledTime: timestamp("scheduled_time"),
  completedAt: timestamp("completed_at"),
  status: varchar("status", { length: 20 }).notNull().default("booked"), // booked, in_progress, completed, cancelled
  createdAt: timestamp("created_at").defaultNow(),
});

// Payment records
export const payments = pgTable("payments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  bookingId: varchar("booking_id", { length: 50 }).notNull(),
  userId: varchar("user_id", { length: 50 }).notNull(),
  amount: decimal("amount", { precision: 10, scale: 2 }).notNull(),
  currency: varchar("currency", { length: 3 }).notNull().default("USD"),
  paymentMethod: varchar("payment_method", { length: 50 }), // card, cash, transfer
  // stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 100 }), // Commented out for now - Stripe disabled
  status: varchar("status", { length: 20 }).notNull(), // pending, completed, failed, refunded
  processedAt: timestamp("processed_at"),
  refundedAt: timestamp("refunded_at"),
  refundAmount: decimal("refund_amount", { precision: 10, scale: 2 }),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Sites table
export const sites = pgTable("sites", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  location: text("location"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Lanes table (weighbridge lanes)
export const lanes = pgTable("lanes", {
  id: serial("id").primaryKey(),
  siteId: integer("site_id").notNull(),
  name: varchar("name", { length: 50 }).notNull(),
  maxCapacity: decimal("max_capacity", { precision: 10, scale: 2 }),
  status: varchar("status", { length: 20 }).notNull().default("active"),
});

// Visits table - updated with proper foreign keys
export const visits = pgTable("visits", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  siteId: integer("site_id").notNull(),
  
  // Add proper foreign key relationships
  orderId: integer("order_id"), // Reference to orders table
  vehicleId: integer("vehicle_id"), // Reference to fleet table
  driverId: integer("driver_id"), // Reference to drivers table
  
  // Keep existing fields for backward compatibility
  subjectType: varchar("subject_type", { length: 20 }).notNull(), // vehicle, driver, visitor
  subjectExternalId: varchar("subject_external_id", { length: 100 }).notNull(),
  plateNumber: varchar("plate_number", { length: 20 }),
  driverName: varchar("driver_name", { length: 100 }),
  entryTime: timestamp("entry_time"),
  exitTime: timestamp("exit_time"),
  orderExternalId: varchar("order_external_id", { length: 100 }),
  supplierExternalId: varchar("supplier_external_id", { length: 100 }),
  
  // Customer and Transporter Information
  customerName: varchar("customer_name", { length: 100 }),
  transporterName: varchar("transporter_name", { length: 100 }),
  clientOrderNumber: varchar("client_order_number", { length: 100 }),
  
  // Weight Measurements
  weighbridgeEntryWeight: decimal("weighbridge_entry_weight", { precision: 10, scale: 2 }),
  weighbridgeExitWeight: decimal("weighbridge_exit_weight", { precision: 10, scale: 2 }),
  deliveryPointEntryWeight: decimal("delivery_point_entry_weight", { precision: 10, scale: 2 }),
  deliveryPointExitWeight: decimal("delivery_point_exit_weight", { precision: 10, scale: 2 }),
  
  status: varchar("status", { length: 20 }).notNull().default("open"), // open, closed
  kanbanStatus: varchar("kanban_status", { length: 20 }).default("pending_arrival"), // staging, pending_arrival, checked_in, departed
  accessDecision: varchar("access_decision", { length: 20 }), // PASS, HOLD, MANUAL_REVIEW
  isNonMatched: boolean("is_non_matched").default(false), // Flag for ANPR non-matched entries
  reasons: text("reasons"),
  laneId: integer("lane_id"),
  startedAt: timestamp("started_at").defaultNow(),
  endedAt: timestamp("ended_at"),
  
  // OCR Processing Fields
  driverPhone: varchar("driver_phone", { length: 20 }),
  driverIdNumber: varchar("driver_id_number", { length: 50 }),
  driverPassportNumber: varchar("driver_passport_number", { length: 50 }),
  driverLicenseNumber: varchar("driver_license_number", { length: 50 }),
  driverLicenseValidTo: varchar("driver_license_valid_to", { length: 50 }),
  driverDateOfBirth: varchar("driver_date_of_birth", { length: 50 }),
  driverSex: varchar("driver_sex", { length: 10 }),
  orderNumber: varchar("order_number", { length: 100 }),
  clientName: varchar("client_name", { length: 200 }),
  
  // Document URLs from OCR
  mineTicketUrl: text("mine_ticket_url"),
  mineTicketNumber: varchar("mine_ticket_number", { length: 100 }),
  driverIdUrl: text("driver_id_url"),
  driverPassportUrl: text("driver_passport_url"),
  driverLicenseUrl: text("driver_license_url"),
  permit1Url: text("permit1_url"),
  permit1Number: varchar("permit1_number", { length: 100 }),
  permit1ValidTo: varchar("permit1_valid_to", { length: 100 }),
  permit2Url: text("permit2_url"),
  permit2Number: varchar("permit2_number", { length: 100 }),
  permit2ValidTo: varchar("permit2_valid_to", { length: 100 }),
  
  // OCR Processing Status
  ocrRequested: boolean("ocr_requested").default(false), 
  ocrRequestedAt: timestamp("ocr_requested_at"), 
  ocrProcessed: boolean("ocr_processed").default(false), 
  ocrProcessedAt: timestamp("ocr_processed_at"), 
  ocrConfidence: integer("ocr_confidence"), // 0-100 confidence score
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Weighbridges/Scales configuration table
export const weighbridges = pgTable("weighbridges", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  description: text("description"),
  deviceId: varchar("device_id", { length: 50 }).notNull().unique(),
  location: varchar("location", { length: 200 }),
  maxCapacity: decimal("max_capacity", { precision: 10, scale: 2 }), // in kg
  unit: varchar("unit", { length: 10 }).notNull().default("kg"),
  precision: decimal("precision", { precision: 5, scale: 3 }).default("0.001"), // measurement precision
  isActive: boolean("is_active").notNull().default(true),
  isOnline: boolean("is_online").notNull().default(false),
  ipAddress: varchar("ip_address", { length: 45 }), // IPv4 or IPv6
  port: integer("port"),
  protocolType: varchar("protocol_type", { length: 20 }).default("modbus"), // modbus, serial, tcp, etc
  configuration: jsonb("configuration"), // Device-specific settings
  lastHeartbeat: timestamp("last_heartbeat"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Weigh sessions table
export const weighSessions = pgTable("weigh_sessions", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  visitId: integer("visit_id").notNull(),
  laneId: integer("lane_id").notNull(),
  orderExternalId: varchar("order_external_id", { length: 100 }),
  clientOrderId: integer("client_order_id"),
  clientOrderNumber: varchar("client_order_number", { length: 100 }),
  ticketNo: varchar("ticket_no", { length: 100 }), // Weighbridge ticket number
  driverId: varchar("driver_id", { length: 50 }), // Driver ID as string
  vehiclePlateNumber: varchar("vehicle_plate_number", { length: 20 }),
  trailer1PlateNumber: varchar("trailer1_plate_number", { length: 20 }), // First trailer plate number
  trailer2PlateNumber: varchar("trailer2_plate_number", { length: 20 }), // Second trailer plate number
  driverName: varchar("driver_name", { length: 100 }),
  operator: varchar("operator", { length: 100 }), // Operator conducting the session
  supplier: varchar("supplier", { length: 100 }), // Supplier/transporter name
  sealNumber: varchar("seal_number", { length: 100 }), // Seal numbers from weighbridge tickets
  shift: varchar("shift", { length: 20 }), // Shift information (Morning, Evening, Night, etc.)
  status: varchar("status", { length: 20 }).notNull().default("active"), // active, closed
  tareWeight: decimal("tare_weight", { precision: 10, scale: 2 }),
  grossWeight: decimal("gross_weight", { precision: 10, scale: 2 }),
  netWeight: decimal("net_weight", { precision: 10, scale: 2 }),
  unit: varchar("unit", { length: 5 }).notNull().default("kg"),
  scaleDeviceId: varchar("scale_device_id", { length: 50 }), // Legacy field for backward compatibility
  entryMethod: varchar("entry_method", { length: 20 }).notNull().default("automatic"), // automatic, manual, uploaded
  startedAt: timestamp("started_at").defaultNow(),
  endedAt: timestamp("ended_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Weigh readings table
export const weighReadings = pgTable("weigh_readings", {
  id: serial("id").primaryKey(),
  sessionId: integer("session_id").notNull(),
  type: varchar("type", { length: 20 }).notNull(), // tare, gross, axle
  value: decimal("value", { precision: 10, scale: 2 }).notNull(),
  stabilityMs: integer("stability_ms"),
  raw: text("raw"),
  recordedAt: timestamp("recorded_at").defaultNow(),
});

// Weighbridge Documents table - for storing weighbridge reports, calibration certificates, etc.
export const weighbridgeDocuments = pgTable("weighbridge_documents", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  weighbridgeId: integer("weighbridge_id"), // Optional - can be null for general documents
  
  // Document metadata
  fileName: varchar("file_name", { length: 255 }).notNull(),
  originalFileName: varchar("original_file_name", { length: 255 }).notNull(),
  fileType: varchar("file_type", { length: 50 }).notNull(), // pdf, xlsx, xls, csv
  fileSize: integer("file_size").notNull(), // in bytes
  mimeType: varchar("mime_type", { length: 100 }).notNull(),
  
  // Object storage path
  objectPath: varchar("object_path", { length: 500 }).notNull(),
  
  // Document categorization
  documentType: varchar("document_type", { length: 50 }).notNull(), // daily_report, monthly_summary, calibration_certificate, transaction_log, maintenance_record
  description: text("description"),
  reportDate: timestamp("report_date"), // For reports - the date the report covers
  
  // Upload information
  uploadedBy: varchar("uploaded_by", { length: 50 }).notNull(),
  isPublic: boolean("is_public").notNull().default(false), // Whether document can be accessed publicly
  
  // Status
  isActive: boolean("is_active").notNull().default(true),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Cameras table
export const cameras = pgTable("cameras", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  siteId: integer("site_id").notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  location: varchar("location", { length: 100 }).notNull(),
  cameraType: varchar("camera_type", { length: 20 }).notNull(), // arrival, departure, general
  category: varchar("category", { length: 20 }).notNull().default("anpr"), // anpr, general
  streamUrl: varchar("stream_url", { length: 255 }),
  apiEndpoint: varchar("api_endpoint", { length: 255 }), // API endpoint for number plate retrieval
  isActive: boolean("is_active").notNull().default(true),
  lastHeartbeat: timestamp("last_heartbeat"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Camera readings table for number plate detections
export const cameraReadings = pgTable("camera_readings", {
  id: serial("id").primaryKey(),
  cameraId: integer("camera_id").notNull(),
  plateNumber: varchar("plate_number", { length: 20 }).notNull(),
  confidence: decimal("confidence", { precision: 3, scale: 2 }), // 0.00 to 1.00
  imageUrl: varchar("image_url", { length: 255 }),
  rawData: jsonb("raw_data"), // Store full API response
  detectedAt: timestamp("detected_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Tickets table
export const tickets = pgTable("tickets", {
  id: serial("id").primaryKey(),
  sessionId: integer("session_id").notNull(),
  ticketNo: varchar("ticket_no", { length: 40 }).notNull(),
  fileId: varchar("file_id", { length: 128 }),
  fileUrl: varchar("file_url", { length: 512 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Audit trail table
export const auditEvents = pgTable("audit_events", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  eventType: varchar("event_type", { length: 50 }).notNull(),
  entityType: varchar("entity_type", { length: 50 }).notNull(),
  entityId: varchar("entity_id", { length: 50 }).notNull(),
  userId: varchar("user_id", { length: 50 }),
  details: jsonb("details"),
  timestamp: timestamp("timestamp").defaultNow(),
});

// Facility Tickets table - comprehensive truck access tracking
export const facilityTickets = pgTable("facility_tickets", {
  id: serial("id").primaryKey(),
  ticketNumber: varchar("ticket_number", { length: 20 }).unique().notNull(), // Format: TK001
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  visitId: integer("visit_id").notNull(), // Foreign key to visits table
  vehicleId: integer("vehicle_id"), // Foreign key to fleet table
  driverId: integer("driver_id"), // Foreign key to drivers table
  
  // Timing information
  checkInAt: timestamp("check_in_at"),
  checkOutAt: timestamp("check_out_at"),
  
  // Status tracking
  status: varchar("status", { length: 20 }).notNull().default("pending_arrival"), // staging, pending_arrival, checked_in, departed, void
  
  // Additional information
  notes: text("notes"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Define relations for new tables
export const tenantsRelations = relations(tenants, ({ many }) => ({
  users: many(users),
  facilities: many(facilities),
}));

export const usersRelations = relations(users, ({ one, many }) => ({
  tenant: one(tenants, {
    fields: [users.tenantId],
    references: [tenants.id],
  }),
  bookings: many(bookings),
  payments: many(payments),
}));

export const facilitiesRelations = relations(facilities, ({ one, many }) => ({
  tenant: one(tenants, {
    fields: [facilities.tenantId],
    references: [tenants.id],
  }),
  services: many(services),
  bookings: many(bookings),
}));

export const servicesRelations = relations(services, ({ one, many }) => ({
  facility: one(facilities, {
    fields: [services.facilityId],
    references: [facilities.id],
  }),
  serviceBookings: many(serviceBookings),
}));

export const bookingsRelations = relations(bookings, ({ one, many }) => ({
  user: one(users, {
    fields: [bookings.userId],
    references: [users.id],
  }),
  facility: one(facilities, {
    fields: [bookings.facilityId],
    references: [facilities.id],
  }),
  serviceBookings: many(serviceBookings),
  payments: many(payments),
}));

export const serviceBookingsRelations = relations(serviceBookings, ({ one }) => ({
  booking: one(bookings, {
    fields: [serviceBookings.bookingId],
    references: [bookings.id],
  }),
  service: one(services, {
    fields: [serviceBookings.serviceId],
    references: [services.id],
  }),
}));

export const paymentsRelations = relations(payments, ({ one }) => ({
  booking: one(bookings, {
    fields: [payments.bookingId],
    references: [bookings.id],
  }),
  user: one(users, {
    fields: [payments.userId],
    references: [users.id],
  }),
}));

// Original relations
export const sitesRelations = relations(sites, ({ many }) => ({
  visits: many(visits),
  lanes: many(lanes),
}));

export const lanesRelations = relations(lanes, ({ one, many }) => ({
  site: one(sites, {
    fields: [lanes.siteId],
    references: [sites.id],
  }),
  sessions: many(weighSessions),
}));

export const visitsRelations = relations(visits, ({ one, many }) => ({
  site: one(sites, {
    fields: [visits.siteId],
    references: [sites.id],
  }),
  sessions: many(weighSessions),
  driverDocuments: many(driverDocuments),
}));

export const weighbridgesRelations = relations(weighbridges, ({ many }) => ({
  sessions: many(weighSessions),
  documents: many(weighbridgeDocuments),
}));

export const weighbridgeDocumentsRelations = relations(weighbridgeDocuments, ({ one }) => ({
  weighbridge: one(weighbridges, {
    fields: [weighbridgeDocuments.weighbridgeId],
    references: [weighbridges.id],
  }),
}));

export const weighSessionsRelations = relations(weighSessions, ({ one, many }) => ({
  visit: one(visits, {
    fields: [weighSessions.visitId],
    references: [visits.id],
  }),
  lane: one(lanes, {
    fields: [weighSessions.laneId],
    references: [lanes.id],
  }),
  readings: many(weighReadings),
  tickets: many(tickets),
}));

export const weighReadingsRelations = relations(weighReadings, ({ one }) => ({
  session: one(weighSessions, {
    fields: [weighReadings.sessionId],
    references: [weighSessions.id],
  }),
}));

export const ticketsRelations = relations(tickets, ({ one }) => ({
  session: one(weighSessions, {
    fields: [tickets.sessionId],
    references: [weighSessions.id],
  }),
}));

export const camerasRelations = relations(cameras, ({ one, many }) => ({
  site: one(sites, {
    fields: [cameras.siteId],
    references: [sites.id],
  }),
  readings: many(cameraReadings),
}));

export const cameraReadingsRelations = relations(cameraReadings, ({ one }) => ({
  camera: one(cameras, {
    fields: [cameraReadings.cameraId],
    references: [cameras.id],
  }),
}));

// Insert schemas
export const insertUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
});

export const insertSiteSchema = createInsertSchema(sites).omit({
  id: true,
  createdAt: true,
});

export const insertVisitSchema = createInsertSchema(visits).omit({
  id: true,
  startedAt: true,
  endedAt: true,
});

export const insertWeighbridgeSchema = createInsertSchema(weighbridges).omit({
  id: true,
  tenantId: true, // Server-controlled
  isOnline: true, // Server-controlled  
  lastHeartbeat: true, // Server-controlled
  createdAt: true,
  updatedAt: true,
});

export const updateWeighbridgeSchema = createInsertSchema(weighbridges).partial().omit({
  id: true,
  tenantId: true, // Never allow changing tenant
  isOnline: true, // Only updated via status endpoint
  lastHeartbeat: true, // Only updated via status endpoint
  createdAt: true,
  updatedAt: true,
});

export const insertWeighbridgeDocumentSchema = createInsertSchema(weighbridgeDocuments).omit({
  id: true,
  tenantId: true, // Server-controlled
  objectPath: true, // Set by server after upload
  uploadedBy: true, // Server-controlled
  createdAt: true,
  updatedAt: true,
});

export const updateWeighbridgeDocumentSchema = createInsertSchema(weighbridgeDocuments).partial().omit({
  id: true,
  tenantId: true, // Never allow changing tenant
  objectPath: true, // Cannot change object path
  uploadedBy: true, // Cannot change uploader
  fileName: true, // Cannot change file name
  fileType: true, // Cannot change file type
  fileSize: true, // Cannot change file size
  mimeType: true, // Cannot change mime type
  createdAt: true,
  updatedAt: true,
});

export const insertWeighSessionSchema = createInsertSchema(weighSessions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertWeighReadingSchema = createInsertSchema(weighReadings).omit({
  id: true,
  recordedAt: true,
});

export const insertTicketSchema = createInsertSchema(tickets).omit({
  id: true,
  createdAt: true,
});

export const insertCameraSchema = createInsertSchema(cameras).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCameraReadingSchema = createInsertSchema(cameraReadings).omit({
  id: true,
  createdAt: true,
});

// Insert schemas for new tables
export const insertTenantSchema = createInsertSchema(tenants).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertFacilitySchema = createInsertSchema(facilities).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertServiceSchema = createInsertSchema(services).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertBookingSchema = createInsertSchema(bookings).omit({
  id: true,
  bookingNumber: true,
  actualCheckIn: true,
  actualCheckOut: true,
  // paymentIntentId: true, // Commented out for now - Stripe disabled
  createdAt: true,
  updatedAt: true,
});

export const insertServiceBookingSchema = createInsertSchema(serviceBookings).omit({
  id: true,
  completedAt: true,
  createdAt: true,
});

export const insertPaymentSchema = createInsertSchema(payments).omit({
  id: true,
  processedAt: true,
  refundedAt: true,
  createdAt: true,
});

// Enhanced user schema for registration
export const insertUserRegistrationSchema = createInsertSchema(users).omit({
  id: true,
  role: true,
  tenantId: true,
  isActive: true,
  emailVerified: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  confirmPassword: z.string().min(6),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Types for new tables
export type InsertTenant = z.infer<typeof insertTenantSchema>;
export type Tenant = typeof tenants.$inferSelect;

export type InsertFacility = z.infer<typeof insertFacilitySchema>;
export type Facility = typeof facilities.$inferSelect;

export type InsertService = z.infer<typeof insertServiceSchema>;
export type Service = typeof services.$inferSelect;

export type InsertBooking = z.infer<typeof insertBookingSchema>;
export type Booking = typeof bookings.$inferSelect;

export type InsertServiceBooking = z.infer<typeof insertServiceBookingSchema>;
export type ServiceBooking = typeof serviceBookings.$inferSelect;

export type InsertPayment = z.infer<typeof insertPaymentSchema>;
export type Payment = typeof payments.$inferSelect;

export type InsertUserRegistration = z.infer<typeof insertUserRegistrationSchema>;

// Enhanced types
export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

export type InsertSite = z.infer<typeof insertSiteSchema>;
export type Site = typeof sites.$inferSelect;

export type InsertVisit = z.infer<typeof insertVisitSchema>;
export type Visit = typeof visits.$inferSelect;

export type InsertWeighbridge = z.infer<typeof insertWeighbridgeSchema>;
export type UpdateWeighbridge = z.infer<typeof updateWeighbridgeSchema>;
export type Weighbridge = typeof weighbridges.$inferSelect;

export type InsertWeighbridgeDocument = z.infer<typeof insertWeighbridgeDocumentSchema>;
export type UpdateWeighbridgeDocument = z.infer<typeof updateWeighbridgeDocumentSchema>;
export type WeighbridgeDocument = typeof weighbridgeDocuments.$inferSelect;

export type InsertWeighSession = z.infer<typeof insertWeighSessionSchema>;
export type WeighSession = typeof weighSessions.$inferSelect;

export type InsertWeighReading = z.infer<typeof insertWeighReadingSchema>;
export type WeighReading = typeof weighReadings.$inferSelect;

export type InsertTicket = z.infer<typeof insertTicketSchema>;
export type Ticket = typeof tickets.$inferSelect;

// Facility tickets insert schema and types
export const insertFacilityTicketSchema = createInsertSchema(facilityTickets).omit({
  id: true,
  ticketNumber: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertFacilityTicket = z.infer<typeof insertFacilityTicketSchema>;
export type FacilityTicket = typeof facilityTickets.$inferSelect;

export type AuditEvent = typeof auditEvents.$inferSelect;

// Email recipient management tables
export const emailRecipients = pgTable("email_recipients", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  role: varchar("role", { length: 50 }).notNull(), // manager, operator, admin, compliance
  reportTypes: text("report_types").array().notNull().default([]), // daily, weekly, monthly, alerts
  active: boolean("active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const emailSchedules = pgTable("email_schedules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  reportType: varchar("report_type", { length: 50 }).notNull(), // daily, weekly, monthly
  schedule: varchar("schedule", { length: 100 }).notNull(), // cron format
  recipients: text("recipients").array().notNull(),
  active: boolean("active").notNull().default(true),
  lastSent: timestamp("last_sent"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const emailLogs = pgTable("email_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  scheduleId: varchar("schedule_id", { length: 100 }),
  recipients: text("recipients").array().notNull(),
  subject: varchar("subject", { length: 255 }).notNull(),
  reportType: varchar("report_type", { length: 50 }).notNull(),
  success: boolean("success").notNull(),
  error: text("error"),
  sentAt: timestamp("sent_at").defaultNow(),
});

// Email schema relations
export const emailRecipientsRelations = relations(emailRecipients, ({ many }) => ({
  logs: many(emailLogs),
}));

export const emailSchedulesRelations = relations(emailSchedules, ({ many }) => ({
  logs: many(emailLogs),
}));

export const emailLogsRelations = relations(emailLogs, ({ one }) => ({
  recipient: one(emailRecipients, {
    fields: [emailLogs.scheduleId],
    references: [emailRecipients.id],
  }),
  schedule: one(emailSchedules, {
    fields: [emailLogs.scheduleId],
    references: [emailSchedules.id],
  }),
}));

// Email insert schemas
export const insertEmailRecipientSchema = createInsertSchema(emailRecipients).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertEmailScheduleSchema = createInsertSchema(emailSchedules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  lastSent: true,
});

export const insertEmailLogSchema = createInsertSchema(emailLogs).omit({
  id: true,
  sentAt: true,
});

// Email types
export type EmailRecipient = typeof emailRecipients.$inferSelect;
export type InsertEmailRecipient = z.infer<typeof insertEmailRecipientSchema>;
export type EmailSchedule = typeof emailSchedules.$inferSelect;
export type InsertEmailSchedule = z.infer<typeof insertEmailScheduleSchema>;
export type EmailLog = typeof emailLogs.$inferSelect;
export type InsertEmailLog = z.infer<typeof insertEmailLogSchema>;

// Planned visits and ETA tracking tables
export const plannedVisits = pgTable("planned_visits", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  siteId: integer("site_id").notNull(),
  externalId: varchar("external_id", { length: 100 }), // From external planning system
  plateNumber: varchar("plate_number", { length: 20 }).notNull(),
  trailer1PlateNumber: varchar("trailer1_plate_number", { length: 20 }),
  trailer2PlateNumber: varchar("trailer2_plate_number", { length: 20 }),
  driverName: varchar("driver_name", { length: 100 }).notNull(),
  driverId: varchar("driver_id", { length: 50 }),
  driverPhone: varchar("driver_phone", { length: 20 }),
  driverPermit: varchar("driver_permit", { length: 50 }),
  permitExpiryDate: timestamp("permit_expiry_date", { mode: "date" }),
  transporterName: varchar("transporter_name", { length: 100 }),
  orderExternalId: varchar("order_external_id", { length: 100 }),
  product: varchar("product", { length: 100 }),
  expectedWeight: varchar("expected_weight", { length: 20 }), // Expected net weight
  
  // ETA and scheduling
  scheduledArrival: timestamp("scheduled_arrival", { mode: "date" }).notNull(),
  estimatedArrival: timestamp("estimated_arrival", { mode: "date" }), // Updated ETA from API
  actualArrival: timestamp("actual_arrival", { mode: "date" }), // When visit actually started
  
  // Status tracking
  status: varchar("status", { length: 20 }).notNull().default("scheduled"), // scheduled, in_transit, arrived, completed, cancelled
  priority: varchar("priority", { length: 20 }).notNull().default("normal"), // urgent, high, normal, low
  
  // Additional info
  specialInstructions: text("special_instructions"),
  contactInfo: varchar("contact_info", { length: 200 }),
  
  // Driver verification
  driverLicenseNumber: varchar("driver_license_number", { length: 50 }),
  licenseImageUrl: varchar("license_image_url", { length: 500 }), // OCR scan image
  faceImageUrl: varchar("face_image_url", { length: 500 }), // Facial recognition image
  verificationStatus: varchar("verification_status", { length: 20 }).notNull().default("pending"), // pending, verified, failed
  verificationMethod: varchar("verification_method", { length: 20 }), // ocr, facial_recognition, manual
  verificationNotes: text("verification_notes"),
  verifiedAt: timestamp("verified_at"),
  verifiedBy: varchar("verified_by", { length: 50 }), // User ID who verified
  
  // Tracking
  visitId: integer("visit_id"), // Links to actual visit when it starts
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

export const etaUpdates = pgTable("eta_updates", {
  id: serial("id").primaryKey(),
  plannedVisitId: integer("planned_visit_id").notNull(),
  previousEta: timestamp("previous_eta", { mode: "date" }),
  newEta: timestamp("new_eta", { mode: "date" }).notNull(),
  updateSource: varchar("update_source", { length: 50 }).notNull(), // api, operator, driver, gps
  reason: varchar("reason", { length: 100 }), // traffic, delay, breakdown, etc
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Planning board relations
export const plannedVisitsRelations = relations(plannedVisits, ({ one, many }) => ({
  site: one(sites, {
    fields: [plannedVisits.siteId],
    references: [sites.id],
  }),
  visit: one(visits, {
    fields: [plannedVisits.visitId],
    references: [visits.id],
  }),
  etaUpdates: many(etaUpdates),
}));

export const etaUpdatesRelations = relations(etaUpdates, ({ one }) => ({
  plannedVisit: one(plannedVisits, {
    fields: [etaUpdates.plannedVisitId],
    references: [plannedVisits.id],
  }),
}));

// Planning board insert schemas
export const insertPlannedVisitSchema = createInsertSchema(plannedVisits).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  actualArrival: true,
  visitId: true,
});

export const insertEtaUpdateSchema = createInsertSchema(etaUpdates).omit({
  id: true,
  updatedAt: true,
});

// Planning board types
export type PlannedVisit = typeof plannedVisits.$inferSelect;
export type InsertPlannedVisit = z.infer<typeof insertPlannedVisitSchema>;
export type EtaUpdate = typeof etaUpdates.$inferSelect;
export type InsertEtaUpdate = z.infer<typeof insertEtaUpdateSchema>;

// Transporters table - transport companies with fleet information
export const transporters = pgTable("transporters", {
  id: serial("id").primaryKey(),
  transporterCode: varchar("transporter_code", { length: 20 }), // Format: T001
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  name: varchar("name", { length: 200 }).notNull(),
  normalizedName: varchar("normalized_name", { length: 200 }).notNull(), // Computed field for deduplication
  contactPerson: varchar("contact_person", { length: 100 }),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 20 }),
  address: text("address"),
  registrationNumber: varchar("registration_number", { length: 50 }),
  taxNumber: varchar("tax_number", { length: 50 }),
  operatingLicense: varchar("operating_license", { length: 50 }),
  licenseExpiryDate: timestamp("license_expiry_date", { mode: "date" }),
  status: varchar("status", { length: 20 }).notNull().default("active"), // active, inactive, suspended
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
}, (table) => ({
  // Unique constraint: transporterCode must be unique per tenant (when provided)
  uniqueTransporterCode: unique("unique_transporter_code").on(table.tenantId, table.transporterCode),
  // Unique constraint: normalized name must be unique per tenant
  uniqueNormalizedName: unique("unique_normalized_name").on(table.tenantId, table.normalizedName),
  // Optional: unique email and phone per tenant if provided
  uniqueEmail: unique("unique_email").on(table.tenantId, table.email),
  uniquePhone: unique("unique_phone").on(table.tenantId, table.phone),
}));

// Fleet table - consolidated vehicles belonging to transporters
export const fleet = pgTable("fleet", {
  id: serial("id").primaryKey(),
  vehicleCode: varchar("vehicle_code", { length: 20 }).unique(), // Format: TK001 for trucks, TR001 for trailers
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  transporterId: integer("transporter_id").notNull(),
  plateNumber: varchar("plate_number", { length: 20 }).notNull(),
  fleetNumber: varchar("fleet_number", { length: 50 }), // Internal fleet identifier
  vehicleType: varchar("vehicle_type", { length: 50 }).notNull(), // truck, trailer, tanker, etc
  make: varchar("make", { length: 50 }),
  model: varchar("model", { length: 50 }),
  year: integer("year"),
  odometer: integer("odometer").default(0), // Current odometer reading in km
  capacity: decimal("capacity", { precision: 10, scale: 2 }), // in tons
  tareWeight: decimal("tare_weight", { precision: 10, scale: 2 }), // in kg
  grossWeight: decimal("gross_weight", { precision: 10, scale: 2 }), // in kg
  fuelType: varchar("fuel_type", { length: 20 }), // diesel, petrol, electric, etc
  registrationExpiry: timestamp("registration_expiry", { mode: "date" }),
  insuranceExpiry: timestamp("insurance_expiry", { mode: "date" }),
  
  // Trailer information
  trailer1Registration: varchar("trailer1_registration", { length: 20 }),
  trailer2Registration: varchar("trailer2_registration", { length: 20 }),
  
  status: varchar("status", { length: 20 }).notNull().default("active"), // active, maintenance, retired
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
}, (table) => ({
  // Unique constraint: plate number must be unique per tenant
  uniquePlateNumber: unique("unique_fleet_plate_number").on(table.tenantId, table.plateNumber),
}));

// Vessels table - ships for maritime cargo operations
export const vessels = pgTable("vessels", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  vesselCode: varchar("vessel_code", { length: 20 }), // Format: V001
  vesselName: varchar("vessel_name", { length: 100 }).notNull(),
  imoNumber: varchar("imo_number", { length: 20 }), // International Maritime Organization number
  callSign: varchar("call_sign", { length: 20 }),
  mmsiNumber: varchar("mmsi_number", { length: 20 }), // Maritime Mobile Service Identity
  vesselType: varchar("vessel_type", { length: 50 }), // bulk_carrier, container_ship, tanker, general_cargo
  
  // Tonnage and dimensions
  grossTonnage: decimal("gross_tonnage", { precision: 12, scale: 2 }),
  netTonnage: decimal("net_tonnage", { precision: 12, scale: 2 }),
  deadweightTonnage: decimal("deadweight_tonnage", { precision: 12, scale: 2 }), // DWT
  lengthOverall: decimal("length_overall", { precision: 8, scale: 2 }), // LOA in meters
  beam: decimal("beam", { precision: 8, scale: 2 }), // Width in meters
  
  // Draft information
  draftLoaded: decimal("draft_loaded", { precision: 6, scale: 2 }), // Draft when fully loaded (meters)
  draftBallast: decimal("draft_ballast", { precision: 6, scale: 2 }), // Draft when in ballast (meters)
  currentDraft: decimal("current_draft", { precision: 6, scale: 2 }), // Current draft
  maxDraft: decimal("max_draft", { precision: 6, scale: 2 }), // Maximum allowable draft
  
  // Loading configuration
  loadingType: varchar("loading_type", { length: 30 }).default("conveyor"), // conveyor, container, grab, pump
  numberOfHolds: integer("number_of_holds"), // Number of cargo holds
  numberOfHatches: integer("number_of_hatches"),
  loadingRate: decimal("loading_rate", { precision: 10, scale: 2 }), // Tons per hour
  
  // Registry and ownership
  flag: varchar("flag", { length: 50 }), // Country of registration
  portOfRegistry: varchar("port_of_registry", { length: 100 }),
  owner: varchar("owner", { length: 200 }),
  operator: varchar("operator", { length: 200 }),
  classification: varchar("classification", { length: 50 }), // Classification society (ABS, DNV GL, Lloyd's)
  yearBuilt: integer("year_built"),
  
  // Current status
  status: varchar("status", { length: 30 }).default("active"), // active, inactive, in_port, at_sea, loading, discharging
  currentBerthId: integer("current_berth_id"),
  currentLatitude: decimal("current_latitude", { precision: 10, scale: 8 }),
  currentLongitude: decimal("current_longitude", { precision: 11, scale: 8 }),
  lastPositionUpdate: timestamp("last_position_update", { mode: "date" }),
  
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
}, (table) => ({
  uniqueVesselCode: unique("unique_vessel_code").on(table.tenantId, table.vesselCode),
  uniqueImo: unique("unique_imo_number").on(table.tenantId, table.imoNumber),
}));

// Berths table - docking positions for vessels
export const berths = pgTable("berths", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  berthCode: varchar("berth_code", { length: 20 }).notNull(), // Format: B01, B02
  berthName: varchar("berth_name", { length: 100 }).notNull(),
  portName: varchar("port_name", { length: 100 }),
  terminalName: varchar("terminal_name", { length: 100 }),
  
  // Physical specifications
  berthLength: decimal("berth_length", { precision: 8, scale: 2 }), // Length in meters
  maxDraft: decimal("max_draft", { precision: 6, scale: 2 }), // Maximum draft in meters
  maxVesselLength: decimal("max_vessel_length", { precision: 8, scale: 2 }),
  maxVesselBeam: decimal("max_vessel_beam", { precision: 8, scale: 2 }),
  maxDeadweight: decimal("max_deadweight", { precision: 12, scale: 2 }),
  
  // Loading equipment
  loadingType: varchar("loading_type", { length: 30 }).default("conveyor"), // conveyor, container_crane, grab, pump
  numberOfCranes: integer("number_of_cranes").default(0),
  numberOfConveyors: integer("number_of_conveyors").default(0),
  loadingCapacity: decimal("loading_capacity", { precision: 10, scale: 2 }), // Tons per hour
  
  // Geographic location
  latitude: decimal("latitude", { precision: 10, scale: 8 }),
  longitude: decimal("longitude", { precision: 11, scale: 8 }),
  
  // Current status
  status: varchar("status", { length: 30 }).default("available"), // available, occupied, maintenance, reserved
  currentVesselId: integer("current_vessel_id"),
  occupiedSince: timestamp("occupied_since", { mode: "date" }),
  expectedAvailable: timestamp("expected_available", { mode: "date" }),
  
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
}, (table) => ({
  uniqueBerthCode: unique("unique_berth_code").on(table.tenantId, table.berthCode),
}));

// Vessel order allocations - links vessels to orders with stack assignments
export const vesselOrderAllocations = pgTable("vessel_order_allocations", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  vesselId: integer("vessel_id").notNull(),
  orderId: integer("order_id").notNull(),
  stackId: integer("stack_id"), // Reference to stock pile/stack
  berthId: integer("berth_id"),
  
  // Cargo details
  allocatedQuantity: decimal("allocated_quantity", { precision: 12, scale: 3 }),
  loadedQuantity: decimal("loaded_quantity", { precision: 12, scale: 3 }).default("0"),
  unit: varchar("unit", { length: 10 }).default("tons"),
  product: varchar("product", { length: 200 }),
  
  // Loading configuration
  loadingType: varchar("loading_type", { length: 30 }).default("conveyor"), // conveyor, container, grab
  loadingSequence: integer("loading_sequence").default(1), // Order of loading
  holdNumber: integer("hold_number"), // Which hold to load into
  
  // Schedule
  scheduledLoadStart: timestamp("scheduled_load_start", { mode: "date" }),
  scheduledLoadEnd: timestamp("scheduled_load_end", { mode: "date" }),
  actualLoadStart: timestamp("actual_load_start", { mode: "date" }),
  actualLoadEnd: timestamp("actual_load_end", { mode: "date" }),
  
  // Status
  status: varchar("status", { length: 30 }).default("pending"), // pending, in_progress, completed, cancelled
  
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Vessel notifications - alerts and notifications for vessel operations
export const vesselNotifications = pgTable("vessel_notifications", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  vesselId: integer("vessel_id"),
  orderId: integer("order_id"),
  berthId: integer("berth_id"),
  
  type: varchar("type", { length: 50 }).notNull(), // eta_update, berthing, loading_start, loading_complete, departure, draft_warning
  priority: varchar("priority", { length: 20 }).default("normal"), // low, normal, high, urgent
  title: varchar("title", { length: 200 }).notNull(),
  message: text("message"),
  
  // Recipients
  recipientType: varchar("recipient_type", { length: 30 }), // all, operator, supervisor, client
  recipientIds: text("recipient_ids").array(), // User IDs
  
  // Status tracking
  isRead: boolean("is_read").default(false),
  readAt: timestamp("read_at", { mode: "date" }),
  isDismissed: boolean("is_dismissed").default(false),
  
  // Timing
  scheduledFor: timestamp("scheduled_for", { mode: "date" }),
  sentAt: timestamp("sent_at", { mode: "date" }),
  
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
});

// Export schemas and types for vessels
export const insertVesselSchema = createInsertSchema(vessels).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertBerthSchema = createInsertSchema(berths).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertVesselOrderAllocationSchema = createInsertSchema(vesselOrderAllocations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertVesselNotificationSchema = createInsertSchema(vesselNotifications).omit({
  id: true,
  createdAt: true,
});

export type Vessel = typeof vessels.$inferSelect;
export type InsertVessel = z.infer<typeof insertVesselSchema>;
export type Berth = typeof berths.$inferSelect;
export type InsertBerth = z.infer<typeof insertBerthSchema>;
export type VesselOrderAllocation = typeof vesselOrderAllocations.$inferSelect;
export type InsertVesselOrderAllocation = z.infer<typeof insertVesselOrderAllocationSchema>;
export type VesselNotification = typeof vesselNotifications.$inferSelect;
export type InsertVesselNotification = z.infer<typeof insertVesselNotificationSchema>;

// Vehicles table for tracking truck/vehicle information (separate from fleet for different use cases)
export const vehicles = pgTable("vehicles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id", { length: 50 }).notNull(),
  plateNumber: varchar("plate_number", { length: 20 }).notNull(),
  vehicleType: varchar("vehicle_type", { length: 50 }).notNull(),
  make: varchar("make", { length: 50 }),
  model: varchar("model", { length: 50 }),
  year: integer("year"),
  color: varchar("color", { length: 30 }),
  vin: varchar("vin", { length: 50 }),
  maxWeight: decimal("max_weight", { precision: 10, scale: 2 }),
  tareWeight: decimal("tare_weight", { precision: 10, scale: 2 }),
  length: decimal("length", { precision: 10, scale: 2 }),
  width: decimal("width", { precision: 10, scale: 2 }),
  height: decimal("height", { precision: 10, scale: 2 }),
  registrationNumber: varchar("registration_number", { length: 50 }),
  insuranceExpiry: timestamp("insurance_expiry"),
  motExpiry: timestamp("mot_expiry"),
  isActive: boolean("is_active").notNull().default(true),
  tenantId: varchar("tenant_id", { length: 50 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const drivers = pgTable("drivers", {
  id: serial("id").primaryKey(),
  driverCode: varchar("driver_code", { length: 20 }).unique(), // Format: D001
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  transporterId: integer("transporter_id").notNull(),
  employeeId: varchar("employee_id", { length: 50 }),
  
  // Identity Information
  idNumber: varchar("id_number", { length: 50 }), // National ID or identity number
  idExpiryDate: timestamp("id_expiry_date", { mode: "date" }), // ID document expiry
  passportNumber: varchar("passport_number", { length: 50 }),
  passportExpiryDate: timestamp("passport_expiry_date", { mode: "date" }),
  
  // Personal Information
  firstName: varchar("first_name", { length: 100 }).notNull(),
  lastName: varchar("last_name", { length: 100 }).notNull(),
  
  // License Information
  licenseNumber: varchar("license_number", { length: 50 }).notNull(),
  licenseClass: varchar("license_class", { length: 10 }), // A, B, C, etc
  licenseExpiry: timestamp("license_expiry", { mode: "date" }),
  
  // PDP (Professional Driving Permit) Information
  pdpNumber: varchar("pdp_number", { length: 50 }),
  pdpExpiry: timestamp("pdp_expiry", { mode: "date" }),
  
  // Medical Certificate Information
  medicalCertNumber: varchar("medical_cert_number", { length: 50 }),
  medicalCertExpiry: timestamp("medical_cert_expiry", { mode: "date" }),
  
  // Contact Information
  phone: varchar("phone", { length: 20 }),
  phoneCountryCode: varchar("phone_country_code", { length: 5 }).default("+1"), // International dialing code
  alternativePhone: varchar("alternative_phone", { length: 20 }),
  alternativePhoneCountryCode: varchar("alternative_phone_country_code", { length: 5 }).default("+1"),
  email: varchar("email", { length: 255 }),
  emergencyContact: varchar("emergency_contact", { length: 200 }),
  emergencyContactCountryCode: varchar("emergency_contact_country_code", { length: 5 }).default("+1"),
  
  // Induction Information
  inductionCompleted: boolean("induction_completed").notNull().default(false),
  inductionAt: timestamp("induction_at", { mode: "date" }),
  inductionRef: text("induction_ref"), // Reference number or ID for induction record
  
  status: varchar("status", { length: 20 }).notNull().default("active"), // active, inactive, suspended
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Driver Documents table - OCR scanned documents linked to visits and drivers
export const driverDocuments = pgTable("driver_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`), // Use UUID for documents
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  visitId: integer("visit_id").notNull(), // Link to visits table
  driverId: integer("driver_id"), // Link to drivers table (nullable until matched)
  orderId: integer("order_id"), // Link to orders table (optional)
  plateNumber: varchar("plate_number", { length: 20 }).notNull(), // For easy lookup
  documentType: varchar("document_type", { length: 20 }).notNull(), // license, passport, medical, id
  imageUrl: text("image_url").notNull(), // Object storage URL
  ocrStatus: varchar("ocr_status", { length: 20 }).notNull().default("pending"), // pending, success, failed
  ocrText: text("ocr_text"), // Raw OCR extracted text
  extractedFields: jsonb("extracted_fields"), // Parsed structured data
  parsedConfidence: integer("parsed_confidence"), // 0-100 confidence score
  takenByUserId: varchar("taken_by_user_id", { length: 50 }).notNull(), // Yard controller who took the photo
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Visitors table - individuals who need site access
export const visitors = pgTable("visitors", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  
  // Identity Information
  firstName: varchar("first_name", { length: 100 }).notNull(),
  lastName: varchar("last_name", { length: 100 }).notNull(),
  fullName: varchar("full_name", { length: 200 }).notNull(),
  idNumber: varchar("id_number", { length: 50 }),
  passportNumber: varchar("passport_number", { length: 50 }),
  
  // Contact Information
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 20 }),
  phoneCountryCode: varchar("phone_country_code", { length: 5 }).default("+1"),
  emergencyContact: varchar("emergency_contact", { length: 200 }),
  emergencyContactPhone: varchar("emergency_contact_phone", { length: 20 }),
  
  // Employment/Company Information
  company: varchar("company", { length: 200 }),
  position: varchar("position", { length: 100 }),
  department: varchar("department", { length: 100 }),
  
  // Access Control
  accessLevel: varchar("access_level", { length: 20 }).notNull().default("basic"), // basic, restricted, contractor, vip
  isRevoked: boolean("is_revoked").notNull().default(false),
  revokedUntil: timestamp("revoked_until", { mode: "date" }),
  revokeReason: text("revoke_reason"),
  
  // Overall Compliance Status
  complianceStatus: varchar("compliance_status", { length: 20 }).notNull().default("pending"), // compliant, non_compliant, expiring, revoked
  lastComplianceCheck: timestamp("last_compliance_check", { mode: "date" }),
  
  status: varchar("status", { length: 20 }).notNull().default("active"), // active, inactive, suspended
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Visitor Documents table - documents uploaded for visitors with expiry tracking
export const visitorDocuments = pgTable("visitor_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  visitorId: integer("visitor_id").notNull(),
  
  // Document Information
  documentType: varchar("document_type", { length: 30 }).notNull(), // license, induction, pdp, medical, passport, id, safety_cert
  documentName: varchar("document_name", { length: 200 }).notNull(),
  documentNumber: varchar("document_number", { length: 100 }),
  
  // File Storage
  fileUrl: text("file_url").notNull(), // Object storage URL
  originalFileName: varchar("original_file_name", { length: 255 }),
  fileMimeType: varchar("file_mime_type", { length: 100 }),
  fileSize: integer("file_size"), // in bytes
  
  // Expiry and Status Tracking
  issuedDate: timestamp("issued_date", { mode: "date" }),
  expiryDate: timestamp("expiry_date", { mode: "date" }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("valid"), // valid, expiring, expired, replaced
  
  // Validation and Verification
  isVerified: boolean("is_verified").notNull().default(false),
  verifiedBy: varchar("verified_by", { length: 50 }), // User ID who verified
  verifiedAt: timestamp("verified_at", { mode: "date" }),
  verificationNotes: text("verification_notes"),
  
  // OCR and Processing (optional)
  ocrStatus: varchar("ocr_status", { length: 20 }).default("not_processed"), // not_processed, pending, success, failed
  ocrText: text("ocr_text"),
  extractedFields: jsonb("extracted_fields"),
  
  // Replacement tracking
  replacesDocumentId: varchar("replaces_document_id", { length: 50 }), // Reference to previous version
  isCurrentVersion: boolean("is_current_version").notNull().default(true),
  
  uploadedBy: varchar("uploaded_by", { length: 50 }).notNull(), // User ID who uploaded
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Visitor Document Notifications table - track expiry notifications sent
export const visitorDocumentNotifications = pgTable("visitor_document_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  visitorId: integer("visitor_id").notNull(),
  documentId: varchar("document_id", { length: 50 }).notNull(),
  
  // Notification Details
  notificationType: varchar("notification_type", { length: 20 }).notNull(), // expiry_warning, expired, renewal_reminder
  daysBefore: integer("days_before"), // How many days before expiry this was sent
  notificationMethod: varchar("notification_method", { length: 20 }).notNull(), // email, sms, system
  
  // Delivery Status
  status: varchar("status", { length: 20 }).notNull().default("pending"), // pending, sent, delivered, failed
  sentAt: timestamp("sent_at", { mode: "date" }),
  deliveredAt: timestamp("delivered_at", { mode: "date" }),
  failureReason: text("failure_reason"),
  
  // Content
  subject: varchar("subject", { length: 255 }),
  message: text("message"),
  
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
});

// Clients table - customers who need transportation
export const clients = pgTable("clients", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  name: varchar("name", { length: 200 }).notNull(),
  type: varchar("type", { length: 20 }).notNull().default("company"), // company, individual
  contactPerson: varchar("contact_person", { length: 100 }),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 20 }),
  address: text("address"),
  registrationNumber: varchar("registration_number", { length: 50 }),
  billingAddress: text("billing_address"),
  taxNumber: varchar("tax_number", { length: 50 }),
  creditLimit: decimal("credit_limit", { precision: 12, scale: 2 }),
  paymentTerms: varchar("payment_terms", { length: 50 }), // net_30, net_15, cash, etc
  status: varchar("status", { length: 20 }).notNull().default("active"), // active, inactive, suspended
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Suppliers table - companies that supply goods/materials
export const suppliers = pgTable("suppliers", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  name: varchar("name", { length: 200 }).notNull(),
  type: varchar("type", { length: 50 }).notNull(), // manufacturer, distributor, raw_materials, etc
  contactPerson: varchar("contact_person", { length: 100 }),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 20 }),
  address: text("address"),
  registrationNumber: varchar("registration_number", { length: 50 }),
  taxNumber: varchar("tax_number", { length: 50 }),
  qualityCertifications: text("quality_certifications").array().default([]), // ISO, HACCP, etc
  paymentTerms: varchar("payment_terms", { length: 50 }),
  status: varchar("status", { length: 20 }).notNull().default("active"), // active, inactive, suspended
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Orders table - transportation orders linking clients, suppliers, and transporters
export const orders = pgTable("orders", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  orderNumber: varchar("order_number", { length: 50 }).notNull().unique(),
  clientId: integer("client_id"), 
  supplierId: integer("supplier_id"),
  transporterId: integer("transporter_id"),
  
  // Add foreign keys to vehicle and driver for proper relationships
  vehicleId: integer("vehicle_id"), // Reference to fleet table
  driverId: integer("driver_id"), // Reference to drivers table
  
  // Product and cargo details
  product: varchar("product", { length: 200 }).notNull(),
  productCode: varchar("product_code", { length: 50 }),
  description: text("description"), // Additional product description
  quantity: decimal("quantity", { precision: 12, scale: 3 }),
  unit: varchar("unit", { length: 10 }).notNull().default("kg"), // kg, tons, m3, liters, etc
  units: varchar("units", { length: 10 }), // Alternative units field for compatibility
  expectedWeight: decimal("expected_weight", { precision: 10, scale: 2 }), // in kg
  expectedDeliveryDate: timestamp("expected_delivery_date"), // Legacy field
  
  // Locations
  originAddress: text("origin_address").notNull(),
  destinationAddress: text("destination_address").notNull(),
  
  // Scheduling
  requestedPickupDate: timestamp("requested_pickup_date", { mode: "date" }),
  requestedDeliveryDate: timestamp("requested_delivery_date", { mode: "date" }),
  actualPickupDate: timestamp("actual_pickup_date", { mode: "date" }),
  actualDeliveryDate: timestamp("actual_delivery_date", { mode: "date" }),
  
  // Pricing
  agreedRate: decimal("agreed_rate", { precision: 10, scale: 2 }),
  rateUnit: varchar("rate_unit", { length: 20 }).default("per_kg"), // per_kg, per_ton, flat_rate
  additionalCharges: decimal("additional_charges", { precision: 10, scale: 2 }).default("0"),
  totalAmount: decimal("total_amount", { precision: 10, scale: 2 }),
  
  // Status and tracking
  status: varchar("status", { length: 20 }).notNull().default("pending"), // pending, confirmed, in_transit, delivered, cancelled
  priority: varchar("priority", { length: 20 }).notNull().default("normal"), // urgent, high, normal, low
  
  // Additional information
  specialInstructions: text("special_instructions"),
  notes: text("notes"), // General notes field
  requiresPermit: boolean("requires_permit").notNull().default(false),
  hazardousMaterial: boolean("hazardous_material").notNull().default(false),
  temperatureControlled: boolean("temperature_controlled").notNull().default(false),
  
  // References
  referenceNumber: varchar("reference_number", { length: 100 }), // Client's reference
  purchaseOrderNumber: varchar("purchase_order_number", { length: 100 }),
  
  // Vessel tracking information
  vesselName: varchar("vessel_name", { length: 100 }),
  vesselImoNumber: varchar("vessel_imo_number", { length: 20 }), // International Maritime Organization number
  vesselType: varchar("vessel_type", { length: 50 }), // bulk_carrier, container_ship, tanker, etc
  vesselFlag: varchar("vessel_flag", { length: 50 }), // Country of registration
  vesselDwt: decimal("vessel_dwt", { precision: 10, scale: 2 }), // Deadweight tonnage
  
  // Port information
  portOfLoading: varchar("port_of_loading", { length: 100 }),
  portOfDischarge: varchar("port_of_discharge", { length: 100 }),
  loadingBerth: varchar("loading_berth", { length: 50 }),
  dischargeBerth: varchar("discharge_berth", { length: 50 }),
  
  // Vessel scheduling
  vesselEta: timestamp("vessel_eta", { mode: "date" }), // Estimated Time of Arrival
  vesselEtd: timestamp("vessel_etd", { mode: "date" }), // Estimated Time of Departure  
  vesselAta: timestamp("vessel_ata", { mode: "date" }), // Actual Time of Arrival
  vesselAtd: timestamp("vessel_atd", { mode: "date" }), // Actual Time of Departure
  
  // Current vessel position and status
  currentLatitude: decimal("current_latitude", { precision: 10, scale: 8 }),
  currentLongitude: decimal("current_longitude", { precision: 11, scale: 8 }),
  lastPositionUpdate: timestamp("last_position_update", { mode: "date" }),
  vesselStatus: varchar("vessel_status", { length: 30 }).default("unknown"), // at_sea, at_anchor, at_berth, loading, discharging
  
  // Progress tracking
  progressStage: varchar("progress_stage", { length: 30 }).default("order_placed"), // order_placed, vessel_assigned, loading, in_transit, arrived, discharging, completed
  progressPercentage: integer("progress_percentage").default(0), // 0-100
  milestones: jsonb("milestones"), // Array of completed milestones with timestamps
  
  // Cargo details
  cargoDescription: text("cargo_description"),
  loadingDate: timestamp("loading_date", { mode: "date" }),
  dischargingDate: timestamp("discharging_date", { mode: "date" }),
  
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Order Weighbridge Tickets table - weighbridge readings linked to orders
export const orderWeighbridgeTickets = pgTable("order_weighbridge_tickets", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  orderId: integer("order_id").notNull(),
  ticketNumber: varchar("ticket_number", { length: 50 }).notNull().unique(),
  weighbridgeId: integer("weighbridge_id").notNull(),
  weighbridgeName: varchar("weighbridge_name", { length: 100 }),
  
  // Weight measurements
  grossWeight: decimal("gross_weight", { precision: 10, scale: 2 }),
  tareWeight: decimal("tare_weight", { precision: 10, scale: 2 }),
  netWeight: decimal("net_weight", { precision: 10, scale: 2 }),
  unit: varchar("unit", { length: 5 }).notNull().default("kg"),
  
  // Variance tracking
  expectedWeight: decimal("expected_weight", { precision: 10, scale: 2 }),
  weightVariance: decimal("weight_variance", { precision: 10, scale: 2 }), // Actual vs expected
  variancePercentage: decimal("variance_percentage", { precision: 5, scale: 2 }),
  
  // Vehicle information
  vehiclePlateNumber: varchar("vehicle_plate_number", { length: 20 }),
  trailerPlateNumber: varchar("trailer_plate_number", { length: 20 }),
  driverName: varchar("driver_name", { length: 100 }),
  
  // Timestamps
  firstWeighTime: timestamp("first_weigh_time"),
  secondWeighTime: timestamp("second_weigh_time"),
  
  // Status and validation
  status: varchar("status", { length: 20 }).notNull().default("pending"), // pending, completed, voided
  isWithinTolerance: boolean("is_within_tolerance").default(true),
  toleranceThreshold: decimal("tolerance_threshold", { precision: 5, scale: 2 }).default("5.0"), // %
  
  operator: varchar("operator", { length: 100 }),
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Order Charges table - line-item charges for orders
export const orderCharges = pgTable("order_charges", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  orderId: integer("order_id").notNull(),
  
  chargeType: varchar("charge_type", { length: 30 }).notNull(), // transport, fuel_surcharge, demurrage, tolls, handling, tax
  description: varchar("description", { length: 200 }).notNull(),
  amount: decimal("amount", { precision: 10, scale: 2 }).notNull(),
  taxAmount: decimal("tax_amount", { precision: 10, scale: 2 }).default("0"),
  totalAmount: decimal("total_amount", { precision: 10, scale: 2 }).notNull(),
  
  unit: varchar("unit", { length: 20 }), // per_kg, per_hour, flat_rate
  quantity: decimal("quantity", { precision: 10, scale: 3 }), // hours, kg, etc
  rate: decimal("rate", { precision: 10, scale: 4 }), // rate per unit
  
  isIncluded: boolean("is_included").notNull().default(true), // false for additional charges
  addedBy: varchar("added_by", { length: 50 }),
  addedAt: timestamp("added_at", { mode: "date" }).defaultNow(),
});

// Order Documents table - documents attached to orders
export const orderDocuments = pgTable("order_documents", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  orderId: integer("order_id").notNull(),
  
  // Document metadata
  documentType: varchar("document_type", { length: 30 }).notNull(), // pod, invoice, weighbridge_ticket, permit, photo, manifest
  fileName: varchar("file_name", { length: 255 }).notNull(),
  originalFileName: varchar("original_file_name", { length: 255 }).notNull(),
  fileUrl: text("file_url").notNull(), // Object storage URL
  mimeType: varchar("mime_type", { length: 100 }).notNull(),
  fileSize: integer("file_size").notNull(), // in bytes
  
  // Document details
  title: varchar("title", { length: 200 }),
  description: text("description"),
  tags: text("tags").array().default([]),
  
  // Status and access
  isPublic: boolean("is_public").notNull().default(false),
  status: varchar("status", { length: 20 }).notNull().default("active"), // active, archived, deleted
  
  uploadedBy: varchar("uploaded_by", { length: 50 }).notNull(),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Order Activity Log table - audit trail and timeline for orders
export const orderActivityLog = pgTable("order_activity_log", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  orderId: integer("order_id").notNull(),
  
  // Activity details
  activityType: varchar("activity_type", { length: 30 }).notNull(), // status_change, weigh_event, document_upload, note_added, alert_triggered
  action: varchar("action", { length: 50 }).notNull(), // created, updated, cancelled, weighed, uploaded, etc
  description: text("description").notNull(),
  
  // Context and metadata
  entityType: varchar("entity_type", { length: 30 }), // order, ticket, document, charge
  entityId: varchar("entity_id", { length: 50 }), // ID of the related entity
  
  // Changes tracking
  oldValue: text("old_value"), // JSON string of old state
  newValue: text("new_value"), // JSON string of new state
  
  // User and system info
  userId: varchar("user_id", { length: 50 }), // null for system actions
  userName: varchar("user_name", { length: 100 }),
  userRole: varchar("user_role", { length: 30 }),
  ipAddress: varchar("ip_address", { length: 45 }),
  userAgent: text("user_agent"),
  
  // Timing and location
  timestamp: timestamp("timestamp", { mode: "date" }).defaultNow(),
  location: varchar("location", { length: 100 }), // weighbridge, office, gate, etc
  
  // Priority and alerting
  priority: varchar("priority", { length: 10 }).notNull().default("normal"), // high, normal, low
  isAlert: boolean("is_alert").notNull().default(false),
  alertLevel: varchar("alert_level", { length: 20 }), // info, warning, error, critical
  
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
});

// Stock Management Tables

// Stock Areas table - geo-fenced areas within ports/facilities for stock management
export const stockAreas = pgTable("stock_areas", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  facilityId: integer("facility_id").notNull(), // Link to facilities table
  
  // Area identification
  areaCode: varchar("area_code", { length: 20 }).notNull(), // A001, B002, etc
  name: varchar("name", { length: 100 }).notNull(),
  description: text("description"),
  areaType: varchar("area_type", { length: 30 }).notNull(), // storage, staging, loading, restricted
  
  // Geo-fencing data
  geoFence: jsonb("geo_fence").notNull(), // Polygon coordinates as GeoJSON
  centerLatitude: decimal("center_latitude", { precision: 10, scale: 8 }),
  centerLongitude: decimal("center_longitude", { precision: 11, scale: 8 }),
  
  // Capacity and limits
  maxCapacity: decimal("max_capacity", { precision: 12, scale: 2 }), // in kg or m3
  capacityUnit: varchar("capacity_unit", { length: 10 }).notNull().default("kg"), // kg, tons, m3, liters
  maxPileHeight: decimal("max_pile_height", { precision: 8, scale: 2 }), // in meters
  
  // Access control
  accessLevel: varchar("access_level", { length: 20 }).notNull().default("standard"), // restricted, standard, public
  requiresPermit: boolean("requires_permit").notNull().default(false),
  
  // Status and operational info
  status: varchar("status", { length: 20 }).notNull().default("active"), // active, inactive, maintenance, full
  operationalHours: jsonb("operational_hours"), // {start: "06:00", end: "22:00"}
  
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
}, (table) => ({
  uniqueAreaCode: unique("unique_area_code").on(table.tenantId, table.areaCode),
}));

// Stacks table - individual stacks within areas (formerly stock piles)
export const stacks = pgTable("stock_piles", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  areaId: integer("area_id").notNull(), // Reference to stock_areas
  
  // Stack identification
  stackCode: varchar("pile_code", { length: 30 }).notNull(), // A001-S1, B002-S3, etc
  name: varchar("name", { length: 100 }).notNull(),
  description: text("description"),
  
  // Material information
  materialType: varchar("material_type", { length: 100 }).notNull(), // coal, iron_ore, grain, containers, etc
  materialGrade: varchar("material_grade", { length: 50 }), // premium, standard, low_grade
  supplier: varchar("supplier", { length: 100 }), // Source supplier
  batchNumber: varchar("batch_number", { length: 50 }),
  
  // Client association
  clientId: integer("client_id"), // Reference to client/customer (order.customerId)
  clientName: varchar("client_name", { length: 100 }), // Client name for display
  
  // Location within area
  positionLatitude: decimal("position_latitude", { precision: 10, scale: 8 }),
  positionLongitude: decimal("position_longitude", { precision: 11, scale: 8 }),
  estimatedArea: decimal("estimated_area", { precision: 10, scale: 2 }), // in m2
  
  // Quantity tracking
  currentQuantity: decimal("current_quantity", { precision: 12, scale: 3 }).notNull().default("0"),
  reservedQuantity: decimal("reserved_quantity", { precision: 12, scale: 3 }).notNull().default("0"), // Allocated but not dispatched
  availableQuantity: decimal("available_quantity", { precision: 12, scale: 3 }).notNull().default("0"), // Stores remaining capacity (maxCapacity - currentQuantity)
  unit: varchar("unit", { length: 10 }).notNull().default("kg"), // kg, tons, m3, pieces
  // Note: maxCapacity is computed as currentQuantity + availableQuantity
  
  // Quality and condition
  qualityGrade: varchar("quality_grade", { length: 20 }), // A, B, C, rejected
  moistureContent: decimal("moisture_content", { precision: 5, scale: 2 }), // percentage
  temperature: decimal("temperature", { precision: 5, scale: 2 }), // Celsius
  lastInspectedAt: timestamp("last_inspected_at", { mode: "date" }),
  
  // Aging and rotation
  createdDate: timestamp("created_date", { mode: "date" }).notNull(), // When pile was created
  expiryDate: timestamp("expiry_date", { mode: "date" }), // For perishable materials
  rotationPriority: integer("rotation_priority").default(1), // FIFO priority (1 = highest)
  
  // Status
  status: varchar("status", { length: 20 }).notNull().default("active"), // active, depleted, quarantined, scheduled_removal
  isQuarantined: boolean("is_quarantined").notNull().default(false),
  quarantineReason: text("quarantine_reason"),
  
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
}, (table) => ({
  uniqueStackCode: unique("unique_pile_code").on(table.tenantId, table.stackCode),
}));

// Stock Allocations table - link orders to specific stock areas/stacks
export const stockAllocations = pgTable("stock_allocations", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  orderId: integer("order_id").notNull(), // Reference to orders table
  stackId: integer("pile_id").notNull(), // Reference to stacks
  
  // Allocation details
  allocatedQuantity: decimal("allocated_quantity", { precision: 12, scale: 3 }).notNull(),
  deliveredQuantity: decimal("delivered_quantity", { precision: 12, scale: 3 }).notNull().default("0"),
  remainingQuantity: decimal("remaining_quantity", { precision: 12, scale: 3 }).notNull(),
  unit: varchar("unit", { length: 10 }).notNull().default("kg"),
  
  // Priority and scheduling
  priority: integer("priority").notNull().default(1), // 1 = highest priority
  scheduledDeliveryDate: timestamp("scheduled_delivery_date", { mode: "date" }),
  requestedDeliverySlot: varchar("requested_delivery_slot", { length: 20 }), // morning, afternoon, evening
  
  // Status tracking
  status: varchar("status", { length: 20 }).notNull().default("allocated"), // allocated, partial_delivery, completed, cancelled
  
  // Allocation metadata
  allocatedBy: varchar("allocated_by", { length: 50 }).notNull(), // User who made allocation
  allocatedAt: timestamp("allocated_at", { mode: "date" }).defaultNow(),
  completedAt: timestamp("completed_at", { mode: "date" }),
  
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Stock Transactions table - track all stock movements (delivered, collected, moved, adjusted)
export const stockTransactions = pgTable("stock_transactions", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  stackId: integer("pile_id").notNull(), // Reference to stacks
  orderId: integer("order_id"), // Optional - for delivery/collection transactions
  allocationId: integer("allocation_id"), // Optional - reference to stock_allocations
  
  // Transaction details
  transactionType: varchar("transaction_type", { length: 20 }).notNull(), // delivery, collection, transfer, adjustment, inspection_loss
  direction: varchar("direction", { length: 10 }).notNull(), // inbound, outbound
  quantity: decimal("quantity", { precision: 12, scale: 3 }).notNull(),
  unit: varchar("unit", { length: 10 }).notNull().default("kg"),
  
  // Weight measurements
  grossWeight: decimal("gross_weight", { precision: 10, scale: 2 }),
  tareWeight: decimal("tare_weight", { precision: 10, scale: 2 }),
  netWeight: decimal("net_weight", { precision: 10, scale: 2 }),
  weighbridgeTicketNumber: varchar("weighbridge_ticket_number", { length: 50 }),
  
  // Vehicle and driver info
  vehiclePlateNumber: varchar("vehicle_plate_number", { length: 20 }),
  driverName: varchar("driver_name", { length: 100 }),
  transporterName: varchar("transporter_name", { length: 100 }),
  
  // Timing
  transactionTime: timestamp("transaction_time", { mode: "date" }).defaultNow(),
  
  // Quality and condition at transaction
  qualityGrade: varchar("quality_grade", { length: 20 }),
  moistureContent: decimal("moisture_content", { precision: 5, scale: 2 }),
  temperature: decimal("temperature", { precision: 5, scale: 2 }),
  
  // References and tracking
  referenceNumber: varchar("reference_number", { length: 100 }), // External reference
  batchNumber: varchar("batch_number", { length: 50 }),
  
  // Transfer details (for stack-to-stack movements)
  sourceStackId: integer("source_pile_id"), // For transfers - source stack
  destinationStackId: integer("destination_pile_id"), // For transfers - destination stack
  transferReason: varchar("transfer_reason", { length: 50 }), // consolidation, quality_segregation, relocation
  
  // System tracking
  operatorId: varchar("operator_id", { length: 50 }).notNull(), // User who recorded transaction
  operatorName: varchar("operator_name", { length: 100 }),
  location: varchar("location", { length: 100 }), // weighbridge, gate, crane_location
  
  notes: text("notes"),
  createdAt: timestamp("created_at", { mode: "date" }).defaultNow(),
  updatedAt: timestamp("updated_at", { mode: "date" }).defaultNow(),
});

// Relations for new tables
export const transportersRelations = relations(transporters, ({ many }) => ({
  fleet: many(fleet),
  drivers: many(drivers),
  orders: many(orders),
}));

export const fleetRelations = relations(fleet, ({ one }) => ({
  transporter: one(transporters, {
    fields: [fleet.transporterId],
    references: [transporters.id],
  }),
}));

export const vehiclesRelations = relations(vehicles, ({ one }) => ({
  user: one(users, {
    fields: [vehicles.userId],
    references: [users.id],
  }),
}));

export const driversRelations = relations(drivers, ({ one, many }) => ({
  transporter: one(transporters, {
    fields: [drivers.transporterId],
    references: [transporters.id],
  }),
  documents: many(driverDocuments),
}));

export const driverDocumentsRelations = relations(driverDocuments, ({ one }) => ({
  visit: one(visits, {
    fields: [driverDocuments.visitId],
    references: [visits.id],
  }),
  driver: one(drivers, {
    fields: [driverDocuments.driverId],
    references: [drivers.id],
  }),
  order: one(orders, {
    fields: [driverDocuments.orderId],
    references: [orders.id],
  }),
}));

export const clientsRelations = relations(clients, ({ many }) => ({
  orders: many(orders),
}));

export const suppliersRelations = relations(suppliers, ({ many }) => ({
  orders: many(orders),
}));

export const ordersRelations = relations(orders, ({ one, many }) => ({
  client: one(clients, {
    fields: [orders.clientId],
    references: [clients.id],
  }),
  supplier: one(suppliers, {
    fields: [orders.supplierId],
    references: [suppliers.id],
  }),
  transporter: one(transporters, {
    fields: [orders.transporterId],
    references: [transporters.id],
  }),
  weighbridgeTickets: many(orderWeighbridgeTickets),
  charges: many(orderCharges),
  documents: many(orderDocuments),
  activityLog: many(orderActivityLog),
  stockAllocations: many(stockAllocations),
}));

// Stock Management Relations
export const stockAreasRelations = relations(stockAreas, ({ one, many }) => ({
  facility: one(facilities, {
    fields: [stockAreas.facilityId],
    references: [facilities.id],
  }),
  stacks: many(stacks),
}));

export const stacksRelations = relations(stacks, ({ one, many }) => ({
  area: one(stockAreas, {
    fields: [stacks.areaId],
    references: [stockAreas.id],
  }),
  allocations: many(stockAllocations),
  transactions: many(stockTransactions),
}));

export const stockAllocationsRelations = relations(stockAllocations, ({ one }) => ({
  order: one(orders, {
    fields: [stockAllocations.orderId],
    references: [orders.id],
  }),
  stack: one(stacks, {
    fields: [stockAllocations.stackId],
    references: [stacks.id],
  }),
}));

export const stockTransactionsRelations = relations(stockTransactions, ({ one }) => ({
  stack: one(stacks, {
    fields: [stockTransactions.stackId],
    references: [stacks.id],
  }),
  order: one(orders, {
    fields: [stockTransactions.orderId],
    references: [orders.id],
  }),
  allocation: one(stockAllocations, {
    fields: [stockTransactions.allocationId],
    references: [stockAllocations.id],
  }),
  sourceStack: one(stacks, {
    fields: [stockTransactions.sourceStackId],
    references: [stacks.id],
  }),
  destinationStack: one(stacks, {
    fields: [stockTransactions.destinationStackId],
    references: [stacks.id],
  }),
}));

export const orderWeighbridgeTicketsRelations = relations(orderWeighbridgeTickets, ({ one }) => ({
  order: one(orders, {
    fields: [orderWeighbridgeTickets.orderId],
    references: [orders.id],
  }),
  weighbridge: one(weighbridges, {
    fields: [orderWeighbridgeTickets.weighbridgeId],
    references: [weighbridges.id],
  }),
}));

export const orderChargesRelations = relations(orderCharges, ({ one }) => ({
  order: one(orders, {
    fields: [orderCharges.orderId],
    references: [orders.id],
  }),
}));

export const orderDocumentsRelations = relations(orderDocuments, ({ one }) => ({
  order: one(orders, {
    fields: [orderDocuments.orderId],
    references: [orders.id],
  }),
}));

export const orderActivityLogRelations = relations(orderActivityLog, ({ one }) => ({
  order: one(orders, {
    fields: [orderActivityLog.orderId],
    references: [orders.id],
  }),
}));

// Visitor relations
export const visitorsRelations = relations(visitors, ({ many }) => ({
  documents: many(visitorDocuments),
  notifications: many(visitorDocumentNotifications),
}));

export const visitorDocumentsRelations = relations(visitorDocuments, ({ one, many }) => ({
  visitor: one(visitors, {
    fields: [visitorDocuments.visitorId],
    references: [visitors.id],
  }),
  notifications: many(visitorDocumentNotifications),
}));

export const visitorDocumentNotificationsRelations = relations(visitorDocumentNotifications, ({ one }) => ({
  visitor: one(visitors, {
    fields: [visitorDocumentNotifications.visitorId],
    references: [visitors.id],
  }),
  document: one(visitorDocuments, {
    fields: [visitorDocumentNotifications.documentId],
    references: [visitorDocuments.id],
  }),
}));

// Insert schemas for new tables
// Function to normalize names for deduplication
export const normalizeTransporterName = (name: string): string => {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ') // Collapse multiple spaces into single space
    .replace(/[^\w\s]/g, ''); // Remove special characters
};

// Base insert schema for transporters
export const insertTransporterSchema = createInsertSchema(transporters).omit({
  id: true,
  tenantId: true, // Server-controlled
  normalizedName: true, // Computed server-side
  createdAt: true,
  updatedAt: true,
});

// Server-side validation schema with advanced business rules
export const serverTransporterValidationSchema = insertTransporterSchema.extend({
  // Require either transporterCode OR (name AND one of email/phone)
  name: z.string().min(1, "Name is required").max(200),
  transporterCode: z.string().max(20).optional(),
  email: z.string().email("Invalid email format").optional().or(z.literal("")),
  phone: z.string().max(20).optional(),
}).refine(
  (data) => {
    // Must have transporterCode OR (name AND one of email/phone)
    if (data.transporterCode && data.transporterCode.length > 0) {
      return true; // Valid if has transporter code
    }
    return data.name && (
      (data.email && data.email.length > 0) || 
      (data.phone && data.phone.length > 0)
    );
  },
  {
    message: "Must provide either transporterCode OR (name AND email/phone)",
    path: ["transporterCode"]
  }
);

export const insertFleetSchema = createInsertSchema(fleet).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertDriverSchema = createInsertSchema(drivers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertDriverDocumentSchema = createInsertSchema(driverDocuments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertClientSchema = createInsertSchema(clients).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertSupplierSchema = createInsertSchema(suppliers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertOrderSchema = createInsertSchema(orders).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertOrderWeighbridgeTicketSchema = createInsertSchema(orderWeighbridgeTickets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertOrderChargeSchema = createInsertSchema(orderCharges).omit({
  id: true,
  addedAt: true,
});

export const insertOrderDocumentSchema = createInsertSchema(orderDocuments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertOrderActivityLogSchema = createInsertSchema(orderActivityLog).omit({
  id: true,
  timestamp: true,
  createdAt: true,
});

export const insertVisitorSchema = createInsertSchema(visitors).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertVisitorDocumentSchema = createInsertSchema(visitorDocuments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertVisitorDocumentNotificationSchema = createInsertSchema(visitorDocumentNotifications).omit({
  id: true,
  createdAt: true,
});

// Visitor enums for type safety
export const visitorAccessLevelEnum = z.enum(['basic', 'restricted', 'contractor', 'vip']);
export const visitorStatusEnum = z.enum(['active', 'inactive', 'suspended']);
export const visitorComplianceStatusEnum = z.enum(['compliant', 'non_compliant', 'expiring', 'revoked', 'pending']);

export const visitorDocumentTypeEnum = z.enum(['license', 'induction', 'pdp', 'medical', 'passport', 'id', 'safety_cert']);
export const visitorDocumentStatusEnum = z.enum(['valid', 'expiring', 'expired', 'replaced']);
export const visitorDocumentOcrStatusEnum = z.enum(['not_processed', 'pending', 'success', 'failed']);

export const visitorNotificationTypeEnum = z.enum(['expiry_warning', 'expired', 'renewal_reminder']);
export const visitorNotificationMethodEnum = z.enum(['email', 'sms', 'system']);
export const visitorNotificationStatusEnum = z.enum(['pending', 'sent', 'delivered', 'failed']);

// Enhanced visitor insert schemas with validation
export const createVisitorSchema = insertVisitorSchema.extend({
  accessLevel: visitorAccessLevelEnum.optional(),
  status: visitorStatusEnum.optional(),
  complianceStatus: visitorComplianceStatusEnum.optional(),
});

export const createVisitorDocumentSchema = insertVisitorDocumentSchema.extend({
  documentType: visitorDocumentTypeEnum,
  status: visitorDocumentStatusEnum.optional(),
  ocrStatus: visitorDocumentOcrStatusEnum.optional(),
  expiryDate: z.coerce.date(),
  issuedDate: z.coerce.date().optional(),
});

export const createVisitorNotificationSchema = insertVisitorDocumentNotificationSchema.extend({
  notificationType: visitorNotificationTypeEnum,
  notificationMethod: visitorNotificationMethodEnum,
  status: visitorNotificationStatusEnum.optional(),
});


// Types for new tables
export type Transporter = typeof transporters.$inferSelect;
export type InsertTransporter = z.infer<typeof insertTransporterSchema>;
export type Fleet = typeof fleet.$inferSelect;
export type InsertFleet = z.infer<typeof insertFleetSchema>;
export type Driver = typeof drivers.$inferSelect;
export type InsertDriver = z.infer<typeof insertDriverSchema>;
export type DriverDocument = typeof driverDocuments.$inferSelect;
export type InsertDriverDocument = z.infer<typeof insertDriverDocumentSchema>;
export type Client = typeof clients.$inferSelect;
export type InsertClient = z.infer<typeof insertClientSchema>;
export type Supplier = typeof suppliers.$inferSelect;
export type InsertSupplier = z.infer<typeof insertSupplierSchema>;
export type Order = typeof orders.$inferSelect;
export type InsertOrder = z.infer<typeof insertOrderSchema>;
export type OrderWeighbridgeTicket = typeof orderWeighbridgeTickets.$inferSelect;
export type InsertOrderWeighbridgeTicket = z.infer<typeof insertOrderWeighbridgeTicketSchema>;
export type OrderCharge = typeof orderCharges.$inferSelect;
export type InsertOrderCharge = z.infer<typeof insertOrderChargeSchema>;
export type OrderDocument = typeof orderDocuments.$inferSelect;
export type InsertOrderDocument = z.infer<typeof insertOrderDocumentSchema>;
export type OrderActivityLog = typeof orderActivityLog.$inferSelect;
export type InsertOrderActivityLog = z.infer<typeof insertOrderActivityLogSchema>;
export type Visitor = typeof visitors.$inferSelect;
export type InsertVisitor = z.infer<typeof insertVisitorSchema>;
export type VisitorDocument = typeof visitorDocuments.$inferSelect;
export type InsertVisitorDocument = z.infer<typeof insertVisitorDocumentSchema>;
export type VisitorDocumentNotification = typeof visitorDocumentNotifications.$inferSelect;
export type InsertVisitorDocumentNotification = z.infer<typeof insertVisitorDocumentNotificationSchema>;
export type Camera = typeof cameras.$inferSelect;
export type InsertCamera = z.infer<typeof insertCameraSchema>;
export type CameraReading = typeof cameraReadings.$inferSelect;
export type InsertCameraReading = z.infer<typeof insertCameraReadingSchema>;

// Kanban board configurations
export const kanbanBoards = pgTable("kanban_boards", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  description: text("description"),
  stages: jsonb("stages").notNull(), // Array of stage configurations
  isDefault: boolean("is_default").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
  createdBy: varchar("created_by", { length: 50 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const kanbanBoardsRelations = relations(kanbanBoards, ({ one }) => ({
  // Add relations if needed
}));

export const insertKanbanBoardSchema = createInsertSchema(kanbanBoards).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type KanbanBoard = typeof kanbanBoards.$inferSelect;
export type InsertKanbanBoard = z.infer<typeof insertKanbanBoardSchema>;

// Allowances table - allocation limits per client/transporter
export const allowances = pgTable("allowances", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  
  // Target entity (client or transporter)
  entityType: varchar("entity_type", { length: 20 }).notNull(), // 'client' or 'transporter'
  entityId: integer("entity_id").notNull(), // References clients.id or transporters.id
  entityName: varchar("entity_name", { length: 200 }).notNull(), // Cached name for display
  
  // Allocation configuration
  allocationType: varchar("allocation_type", { length: 20 }).notNull(), // 'trucks' or 'tons'
  allocationAmount: decimal("allocation_amount", { precision: 10, scale: 2 }).notNull(),
  periodType: varchar("period_type", { length: 10 }).notNull(), // 'daily' or 'weekly'
  
  // 7-day period configuration
  startDate: timestamp("start_date", { mode: "date" }).notNull(),
  endDate: timestamp("end_date", { mode: "date" }).notNull(),
  
  // Status and tracking
  status: varchar("status", { length: 20 }).notNull().default("active"), // active, expired, suspended
  notes: text("notes"),
  
  // Metadata
  createdBy: varchar("created_by", { length: 50 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Allowance usage tracking table
export const allowanceUsage = pgTable("allowance_usage", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  allowanceId: integer("allowance_id").notNull(),
  
  // Usage period (daily tracking within the allowance period)
  usageDate: timestamp("usage_date", { mode: "date" }).notNull(),
  
  // Usage tracking
  trucksUsed: integer("trucks_used").notNull().default(0),
  tonnageUsed: decimal("tonnage_used", { precision: 10, scale: 2 }).notNull().default("0.00"),
  
  // References to actual weighbridge sessions
  visitIds: integer("visit_ids").array().default([]), // Array of visit IDs that contributed to this usage
  
  // Calculated fields
  remainingTrucks: integer("remaining_trucks"),
  remainingTonnage: decimal("remaining_tonnage", { precision: 10, scale: 2 }),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Relations
export const allowancesRelations = relations(allowances, ({ many }) => ({
  usage: many(allowanceUsage),
}));

export const allowanceUsageRelations = relations(allowanceUsage, ({ one }) => ({
  allowance: one(allowances, {
    fields: [allowanceUsage.allowanceId],
    references: [allowances.id],
  }),
}));

// Insert schemas
export const insertAllowanceSchema = createInsertSchema(allowances).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertAllowanceUsageSchema = createInsertSchema(allowanceUsage).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Stock Management Insert Schemas
export const insertStockAreaSchema = createInsertSchema(stockAreas).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertStackSchema = createInsertSchema(stacks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const createStockPileSchema = insertStackSchema.extend({
  unit: z.enum(['kg', 'tons', 'm3', 'pieces']),
  status: z.enum(['active', 'depleted', 'quarantined', 'scheduled_removal']).optional(),
  qualityGrade: z.enum(['A', 'B', 'C', 'rejected']).optional(),
  createdDate: z.coerce.date(), 
});


export const insertStockAllocationSchema = createInsertSchema(stockAllocations).omit({
  id: true,
  allocatedAt: true,
  createdAt: true,
  updatedAt: true,
});

export const insertStockTransactionSchema = createInsertSchema(stockTransactions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Enhanced stock management schemas with validation
export const createStockAreaSchema = insertStockAreaSchema.extend({
  geoFence: z.any(), // GeoJSON polygon
  capacityUnit: z.enum(['kg', 'tons', 'm3', 'liters']),
  areaType: z.enum(['storage', 'staging', 'loading', 'restricted']),
  accessLevel: z.enum(['restricted', 'standard', 'public']),
  status: z.enum(['active', 'inactive', 'maintenance', 'full']).optional(),
});

export const createStackSchema = insertStackSchema.extend({
  unit: z.enum(['kg', 'tons', 'm3', 'pieces']),
  status: z.enum(['active', 'depleted', 'quarantined', 'scheduled_removal']).optional(),
  qualityGrade: z.enum(['A', 'B', 'C', 'rejected']).optional(),
  createdDate: z.coerce.date(),
  // maxCapacity is not in the database table, but we accept it in the form and compute availableQuantity from it
  maxCapacity: z.union([z.string(), z.number(), z.null()]).transform(v => v ? String(v) : null).optional(),
});

// Client-facing schema for stack creation (tenantId added by backend from auth)
export const createStackInputSchema = createStackSchema.omit({ tenantId: true }).extend({
  currentQuantity: z.union([z.string(), z.number()]).transform(v => String(v)),
  reservedQuantity: z.union([z.string(), z.number()]).transform(v => String(v)).optional(),
  maxCapacity: z.union([z.string(), z.number(), z.null()]).transform(v => v ? String(v) : null).optional(),
});

export const createStockAllocationSchema = insertStockAllocationSchema.extend({
  status: z.enum(['allocated', 'partial_delivery', 'completed', 'cancelled']).optional(),
  unit: z.enum(['kg', 'tons', 'm3', 'pieces']),
});

export const createStockTransactionSchema = insertStockTransactionSchema.extend({
  transactionType: z.enum(['delivery', 'collection', 'transfer', 'adjustment', 'inspection_loss']),
  direction: z.enum(['inbound', 'outbound']),
  unit: z.enum(['kg', 'tons', 'm3', 'pieces']),
  qualityGrade: z.enum(['A', 'B', 'C', 'rejected']).optional(),
  transferReason: z.enum(['consolidation', 'quality_segregation', 'relocation']).optional(),
});

// Export types
export type Allowance = typeof allowances.$inferSelect;
export type InsertAllowance = z.infer<typeof insertAllowanceSchema>;
export type AllowanceUsage = typeof allowanceUsage.$inferSelect;
export type InsertAllowanceUsage = z.infer<typeof insertAllowanceUsageSchema>;

export const transportationHistory = pgTable("transportation_history", {
  id: serial("id").primaryKey(),
  tenantId: varchar("tenant_id", { length: 50 }).notNull(),
  visitId: integer("visit_id").notNull(), // Reference to original visit
  
  plateNumber: varchar("plate_number", { length: 20 }).notNull(),
  driverName: varchar("driver_name", { length: 100 }),
  transporterName: varchar("transporter_name", { length: 100 }),
  
  orderExternalId: varchar("order_external_id", { length: 100 }),
  product: varchar("product", { length: 200 }),
  clientOrderNumber: varchar("client_order_number", { length: 100 }),
  
  actualCheckedInTime: timestamp("actual_checked_in_time"), // From ANPR entry detection
  actualExitTime: timestamp("actual_exit_time"), // From ANPR exit detection
  timeInLot: integer("time_in_lot"), // Calculated in minutes: exit - checked_in
  
  weighbridgeEntryWeight: decimal("weighbridge_entry_weight", { precision: 10, scale: 2 }),
  weighbridgeExitWeight: decimal("weighbridge_exit_weight", { precision: 10, scale: 2 }),
  netWeight: decimal("net_weight", { precision: 10, scale: 2 }),
  
  isNonMatched: boolean("is_non_matched").default(false),
  accessDecision: varchar("access_decision", { length: 20 }),
  
  migratedAt: timestamp("migrated_at").defaultNow(), // When moved from visits to history
  originalCreatedAt: timestamp("original_created_at"), // Original visit creation time
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Relations
export const transportationHistoryRelations = relations(transportationHistory, ({ one }) => ({
  visit: one(visits, {
    fields: [transportationHistory.visitId],
    references: [visits.id],
  }),
}));

// Insert schema
export const insertTransportationHistorySchema = createInsertSchema(transportationHistory).omit({
  id: true,
  migratedAt: true,
  createdAt: true,
  updatedAt: true,
});

// Types
export type TransportationHistory = typeof transportationHistory.$inferSelect;
export type InsertTransportationHistory = z.infer<typeof insertTransportationHistorySchema>;

// Stock Management Types
export type StockArea = typeof stockAreas.$inferSelect;
export type InsertStockArea = z.infer<typeof insertStockAreaSchema>;
export type Stack = typeof stacks.$inferSelect;
export type InsertStack = z.infer<typeof insertStackSchema>;
export type StockAllocation = typeof stockAllocations.$inferSelect;
export type InsertStockAllocation = z.infer<typeof insertStockAllocationSchema>;
export type StockTransaction = typeof stockTransactions.$inferSelect;
export type InsertStockTransaction = z.infer<typeof insertStockTransactionSchema>;
